// src/pages/B2B/event-reports/components/ReportListTable.tsx
import React from 'react';
import { formatTzWindow } from '../utils/formatTzWindow';
import type { EventReportRecord } from '../types';

interface Props {
  items: EventReportRecord[];
  loading: boolean;
  onDelete: (id: string) => void;
}

function formatCreated(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ReportListTable({ items, loading, onDelete }: Props): React.ReactElement {
  if (loading && items.length === 0) {
    return <div className="py-12 text-center text-gray-500 text-sm">Loading…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="py-16 text-center text-gray-500 text-sm">
        No reports yet. Click <span className="text-violet-300">New Report</span> to generate the first one.
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
      <div className="hidden md:grid grid-cols-[2fr_1.3fr_2.5fr_1.3fr_2fr_8rem] gap-4 px-5 py-3 border-b border-gray-800">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Event</p>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</p>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Window</p>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Generated</p>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Downloads</p>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</p>
      </div>
      {items.map((r, i) => (
        <div
          key={r.id}
          className={`grid grid-cols-1 md:grid-cols-[2fr_1.3fr_2.5fr_1.3fr_2fr_8rem] gap-4 px-5 py-4 hover:bg-gray-800/50 ${i < items.length - 1 ? 'border-b border-gray-800' : ''}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            {r.image_url ? (
              <img src={r.image_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-gray-700" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600 to-pink-500" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{r.event_name}</p>
              {r.location_label && (
                <p className="text-xs text-gray-500 truncate">{r.location_label}</p>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-400 self-center">
            {r.report_type === 'report_for_businesses' ? 'Report for Businesses' : r.report_type}
          </p>
          <p className="text-xs text-gray-300 self-center">
            {formatTzWindow(r.from_at, r.to_at, r.timezone)}
          </p>
          <div className="self-center">
            <p className="text-xs text-gray-300">{formatCreated(r.created_at)}</p>
            <p className="text-[11px] text-gray-500">{r.created_by_email ?? '—'}</p>
          </div>
          <div className="flex items-center gap-2 self-center">
            {r.csv_url && (
              <a
                href={r.csv_url}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="px-2.5 py-1 text-xs font-medium text-violet-300 bg-violet-900/30 border border-violet-700/40 rounded-lg hover:bg-violet-900/50 no-underline"
              >
                CSV
              </a>
            )}
            {r.pdf_url && (
              <a
                href={r.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="px-2.5 py-1 text-xs font-medium text-pink-300 bg-pink-900/30 border border-pink-700/40 rounded-lg hover:bg-pink-900/50 no-underline"
              >
                PDF
              </a>
            )}
          </div>
          <div className="self-center">
            <button
              onClick={() => onDelete(r.id)}
              className="px-2.5 py-1 text-xs font-medium text-gray-400 border border-gray-700 rounded-lg hover:text-red-300 hover:border-red-700/50"
            >
              Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ReportListTable;
